<template>
  <div
    ref="mapRef"
    class="relative order-1 md:order-2 h-1/3 md:h-full md:flex md:flex-col w-full md:w-3/5 lg:w-2/3 bg-black"
  ></div>
  <transition name="fade">
    <div
      v-if="locationsStore.loading"
      class="w-full h-full bg-gray-900 absolute opacity-70 transition-all"
    ></div>
  </transition>
  <transition name="fade">
    <div
      v-if="locationsStore.loading"
      class="transition-all shadow-lg shadow-green-200/20 flex px-5 py-4 pt-4.5 animate-gradient-x bg-green-100 bg-gradient-to-r from-green-300 rounded-full absolute top-6 left-1/4 sm:left-1/2 transform sm:-translate-x-1/2"
    >
      <p class="text-sm font-semibold text-black leading-6">
        Loading Locations
      </p>
      <div role="status" class="pl-3">
        <svg
          aria-hidden="true"
          class="w-5 h-5 text-black animate-spin dark:text-black fill-white"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </transition>
  <div
    ref="infoBoxRef"
    v-show="locationsStore.focusedLocation"
    class="focus:outline-none"
  >
    <div
      v-if="locationsStore.focusedLocation"
      class="col-span-1 divide-y divide-gray-300 rounded-lg bg-white shadow"
    >
      <div
        class="flex w-full items-center justify-between space-x-6 p-2 md:p-6 w-64 w-auto text-center"
      >
        <div class="flex-1 truncate">
          <div class="flex items-start space-x-3">
            <h3 class="truncate text-lg font-medium text-gray-900 text-center">
              {{ locationsStore.focusedLocation.name }}
            </h3>
            <p
              v-if="locationsStore.focusedLocation.official_partner"
              class="inline-block rounded w- px-1.5 py-0.5 pb-0 leading-5 text-xs font-medium text-green-800 bg-green-300"
            >
              Official Partner
            </p>
            <p
              v-else-if="locationsStore.focusedLocation.verified"
              class="inline-block rounded w- px-1.5 py-0.5 pb-0 leading-5 text-xs font-medium text-green-800 bg-gray-300"
            >
              Verified
            </p>
            <p
              v-else
              class="text-gray-500 inline-block rounded text-xs font-medium italic"
            >
              (Unverified)
            </p>
          </div>
          <p
            class="mt-1 truncate text-sm text-gray-500 block whitespace-nowrap"
          >
            {{ locationsStore.focusedLocation.full_address }}
          </p>
        </div>
      </div>
      <div>
        <div class="flex divide-x divide-gray-50">
          <div class="flex w-0 flex-1 relative">
            <a
              @click="viewDetailsClicked"
              href="#"
              class="focus:outline-none hover:bg-gray-50 z-100 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 md:py-4 text-sm text-gray-900"
            >
              View Details
            </a>
          </div>
          <div class="flex w-0 flex-1">
            <a
              :href="
                'https://maps.google.com/?q=' +
                locationsStore.focusedLocation.full_address
              "
              target="_blank"
              class="focus:outline-none hover:bg-gray-50 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-2 md:py-4 text-sm text-gray-900"
            >
              Get Directions
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col items-center">
      <div
        class="border-solid border-t-white border-t-8 border-x-transparent border-x-8 border-b-0"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { Loader } from "@googlemaps/js-api-loader";
import { ref, watch, onMounted } from "vue";
import { useConfigStore } from "../stores/configStore";
import { useLocationsStore } from "../stores/locationsStore";
import { useLocationTermStore } from "../stores/locationTermStore";
import { useMapStore } from "../stores/mapStore";
import { _ } from "lodash";
import { event as gtagEvent } from "vue-gtag";

const configStore = useConfigStore();
const locationsStore = useLocationsStore();
const locationTermStore = useLocationTermStore();
const mapStore = useMapStore();

const loader = new Loader({
  apiKey: configStore.config.google.apiKey,
  version: "weekly",
});
const mapsLibrary = loader.importLibrary("maps");
const coreLibrary = loader.importLibrary("core");
const markerLibrary = loader.importLibrary("marker");

const mapRef = ref(null);
const infoBoxRef = ref(null);
const isDesktop = () => screen.width > 760;
let markers = {};

function focusMarker(marker, location) {
  marker.content.insertBefore(infoBoxRef.value, marker.content.firstChild);
  marker.zIndex = 30;
  marker.content.classList.add("focused");
}

function unfocusMarker(marker, location) {
  marker.content.classList.remove("focused");
  marker.zIndex = getZIndexForLocation(location);
}

function getZIndexForLocation(location) {
  if (location.official_partner) {
    return 20;
  } else if (location.verified) {
    return 10;
  } else {
    return null;
  }
}

function getMarkerIconClassForLocation(location) {
  if (location.official_partner) {
    return "official-partner";
  } else if (location.verified) {
    return "verified";
  } else {
    return "unverified";
  }
}

function viewDetailsClicked() {
  locationsStore.detailExpanded = true;
  trackLocationEvent(locationsStore.focusedLocation, "Venue Details Viewed");
}

function trackLocationEvent(location, event, properties = {}) {
  gtagEvent(event, {
    simulator_location_id: location.id,
    simulator_location_name: location.name,
    simulator_location_postal_code: location.postal_code,
    simulator_location_city: location.city,
    simulator_location_subdivision_code: location.subdivision_code,
    simulator_location_country_code: location.country_code,
    simulator_location_official_partner: location.official_partner,
    simulator_location_verified: location.verified,
    ...properties,
  });
}

onMounted(async () => {
  const [{ Size }, { Map, InfoWindow }, { AdvancedMarkerElement }] =
    await Promise.all([coreLibrary, mapsLibrary, markerLibrary]);

  const map = new Map(mapRef.value, {
    zoomControl: isDesktop(),
    backgroundColor: "black",
    minZoom: 3,
    maxZoom: 15,
    disableDefaultUI: false,
    mapTypeControl: false,
    disableDoubleClickZoom: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: "greedy",
    clickableIcons: false,
    mapId: "823c3a3818151357",
  });

  mapStore.setMap(map);

  map.addListener(
    "bounds_changed",
    _.debounce(function () {
      if (map.getBounds() == null) {
        return;
      }

      locationsStore.fetchLocationsFromLatLon(
        map.getCenter().lat(),
        map.getCenter().lng(),
        map.getBounds(),
      );
    }, 400),
  );
  map.addListener("click", () => {
    locationsStore.$patch({
      focusedLocation: null,
      detailExpanded: false,
    });
  });

  const infoBox = new InfoWindow({ pixelOffset: new Size(0, 30) });
  infoBox.setContent(infoBoxRef.value);

  watch(
    () => locationsStore.locations,
    (locations) => {
      let locationIds = [];

      locations.forEach((location) => {
        let marker = markers[location.id];

        if (!marker) {
          const markerContent = document.createElement("div");
          markerContent.classList.add("marker");
          const markerImg = document.createElement("img");
          markerImg.classList.add("icon");
          markerImg.classList.add(getMarkerIconClassForLocation(location));

          markerContent.appendChild(markerImg);

          marker = new AdvancedMarkerElement({
            map: map,
            position: {
              lat: location.latitude,
              lng: location.longitude,
            },
            content: markerContent,
            zIndex: getZIndexForLocation(location),
          });

          marker.addListener("click", () => {
            locationsStore.focusedLocation = location;
            trackLocationEvent(location, "Venue Marker Clicked");
          });

          markers[location.id] = marker;
        } else {
          marker.element.hidden = false;
        }

        locationIds.push(location.id);
      });

      Object.entries(markers).forEach(([id, marker]) => {
        if (!locationIds.includes(id)) {
          marker.element.hidden = true;
        }
      });
    },
    { immediate: true },
  );

  watch(
    () => locationsStore.focusedLocation,
    (newLocation, oldLocation) => {
      if (oldLocation) {
        unfocusMarker(markers[oldLocation.id], oldLocation);
      }

      if (newLocation) {
        focusMarker(markers[newLocation.id], newLocation);
      }
    },
  );

  watch(
    () => locationTermStore.coords,
    (coords) => {
      if (coords) {
        map.panTo({
          lat: coords.latitude,
          lng: coords.longitude,
        });

        // Pan down a little further so that the info box is not covered by the search bar.
        map.panBy(0, -75);
      }
    },
  );
});
</script>

<style>
.marker {
  @apply flex flex-col items-center relative focus:outline-none;

  .icon {
    @apply w-8 z-0;

    &.unverified {
      content: url("@/venue_locator/assets/images/non-enabled.svg");
    }
    &.verified {
      content: url("@/venue_locator/assets/images/pinseeker-enabled.svg");
    }
    &.official-partner {
      content: url("@/venue_locator/assets/images/official-partner.svg");
    }
  }

  .info-box {
    @apply hidden;
  }
}

.marker.focused {
  .icon {
    @apply hidden;
  }

  .info-box {
    @apply flex;
  }
}
</style>
